/**
 * act-as-customer-surfaces.ts — React Query hooks for act-as customer surfaces.
 *
 * All hooks use actAsClient (RPT + X-Act-As-Org) to call the same /api/v3/*
 * endpoints Partner console uses. The BE ActAsOrg middleware transparently
 * routes these requests to the target org's tenant context.
 *
 * Surfaces:
 *   - CDN domain list  → GET /api/v3/services/:id/cdn/domains
 *   - WAAP domain list → GET /api/v3/services/:id/waap/domains
 */

import { useQuery } from '@tanstack/react-query';
import { actAsClient } from '@/api/act-as-client';

/* ── CDN domain types (mirrors Partner /services/:id/cdn/domains) ─────────── */

export interface ActAsCDNDomain {
  id: string;
  /** FQDN — the v3 body field is `domain` (NOT `domain_name`). */
  domain: string;
  status: string;
  /** Provider/vendor slug, e.g. "raven", "eagle", "hawk". */
  provider_name?: string;
  cname?: string;
  created_at: string;
  updated_at: string;
}

export interface ActAsCDNDomainsResponse {
  data: ActAsCDNDomain[];
  pagination?: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

/* ── WAAP domain types (mirrors Partner /services/:id/waap/domains) ──────── */

export interface ActAsWAAPDomain {
  id: string;
  /** FQDN — the v3 body field is `domain` (NOT `domain_name`). */
  domain: string;
  status: string;
  enabled?: boolean;
  origin?: string;
  created_at: string;
  updated_at: string;
}

/** v3 envelope: `{ data: [...], pagination }` — the array lives under `data`. */
export interface ActAsWAAPDomainsResponse {
  data: ActAsWAAPDomain[];
  pagination?: { total: number; page: number; per_page: number };
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

export const actAsKeys = {
  cdnDomains: (serviceId: string) => ['act-as', 'cdn-domains', serviceId] as const,
  waapDomains: (serviceId: string) => ['act-as', 'waap-domains', serviceId] as const,
};

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * GET /api/v3/services/:id/cdn/domains — CDN domain list for this service
 * in the target org's context. Requires an active act-as session.
 */
export function useActAsCDNDomains(serviceId: string, enabled = true) {
  return useQuery({
    queryKey: actAsKeys.cdnDomains(serviceId),
    queryFn: async (): Promise<ActAsCDNDomain[]> => {
      const res = await actAsClient.get<ActAsCDNDomainsResponse>(
        `/services/${serviceId}/cdn/domains`,
      );
      return res.data.data ?? [];
    },
    enabled: Boolean(serviceId) && enabled,
    // Don't stale-refetch automatically — act-as data is on-demand.
    staleTime: 30_000,
  });
}

/**
 * GET /api/v3/services/:id/waap/domains — WAAP website list for this service
 * in the target org's context. Requires an active act-as session.
 */
export function useActAsWAAPDomains(serviceId: string, enabled = true) {
  return useQuery({
    queryKey: actAsKeys.waapDomains(serviceId),
    queryFn: async (): Promise<ActAsWAAPDomain[]> => {
      const res = await actAsClient.get<ActAsWAAPDomainsResponse>(
        `/services/${serviceId}/waap/domains`,
      );
      return res.data.data ?? [];
    },
    enabled: Boolean(serviceId) && enabled,
    staleTime: 30_000,
  });
}
